import React from 'react';
import type { OfficialResult } from '../types';
import { SparklesIcon, StarIcon } from './icons/PlaybackIcons';

interface OfficialResultsProps {
    results: OfficialResult[];
    onRowClick?: (athleteId: string) => void;
    spotlightedAthleteId?: string | null;
    noExternalLink?: boolean; // True when the category has no externalId (auto-linker hasn't matched it)
}

const OfficialResults: React.FC<OfficialResultsProps> = ({ results, onRowClick, spotlightedAthleteId, noExternalLink }) => {
    if (!results || results.length === 0) {
        return (
            <div className="flex-grow flex flex-col items-center justify-center text-gray-500 p-6 text-center border border-dashed border-gray-700 rounded-lg">
                <SparklesIcon className="w-8 h-8 text-gray-600 mb-3" />
                <p className="mb-2 font-medium text-gray-400">No results found.</p>
                {noExternalLink ? (
                    <>
                        <p className="text-xs">This event is not yet linked to the USA Cycling database.</p>
                        <p className="text-xs mt-1 text-gray-600">The auto-linker runs every 12 hours — check back after results are posted.</p>
                    </>
                ) : (
                    <p className="text-xs">Official results have not been posted yet, or this event is not linked to the USA Cycling database.</p>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-grow min-h-0">
            <div className="overflow-y-auto pr-2 -mr-2">
                <table className="w-full text-left table-fixed border-collapse">
                    <thead className="sticky top-0 bg-gray-800 z-10 shadow-sm border-b border-gray-700">
                        <tr className="text-xs text-gray-400 uppercase">
                            <th className="p-2 w-12 text-center">Pl</th>
                            <th className="p-2 w-2/5">Name</th>
                            <th className="p-2 w-1/4">Team</th>
                            <th className="p-2 w-1/5 text-right">Time</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {results.map((result, idx) => {
                            const isSpotlighted = result.matchedAthleteId && result.matchedAthleteId === spotlightedAthleteId;
                            return (
                                <tr 
                                    key={result.id || idx} 
                                    onClick={() => result.matchedAthleteId && onRowClick?.(result.matchedAthleteId)}
                                    className={`border-b transition-colors ${
                                        isSpotlighted 
                                            ? 'bg-yellow-900/30 border-yellow-700/50' 
                                            : 'border-gray-700/50 ' + (result.matchedAthleteId ? 'cursor-pointer hover:bg-gray-700/50' : '')
                                    }`}
                                >
                                <td className="p-2 text-center font-bold text-gray-300">
                                    {result.place > 0 ? result.place : (result.status || '—')}
                                </td>
                                <td className="p-2 truncate font-medium text-gray-200">
                                    <div className="flex items-center gap-2">
                                        {isSpotlighted && <StarIcon className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
                                        <span className="truncate">{result.name}</span>
                                        {result.isMatched && (
                                            <span className="text-[10px] bg-cyan-900/40 text-cyan-400 px-1.5 py-0.5 rounded border border-cyan-700/50 flex-shrink-0" title="Telemetry Track Available">DATA LINKED</span>
                                        )}
                                    </div>
                                </td>
                                <td className="p-2 truncate text-gray-400 text-xs">{result.team || '—'}</td>
                                <td className="p-2 text-right font-mono text-gray-300 text-xs">{result.timeGap ? result.timeGap : result.finishTime || '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OfficialResults;